import React from 'react';
import { Box, Typography, useTheme, Grid } from '@mui/material';
import { FaExclamationTriangle, FaUser, FaPhone, FaStickyNote } from 'react-icons/fa';
import { SectionProps, CarePlanFieldPath } from '../../../../types/ndisSection/carePlanInfoTypes';
import { SectionHeader, CareTextField } from '../Components';

const getNestedValue = (obj: any, path: string): string => {
  if (!obj) return 'Not provided';
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return 'Not provided';
    }
  }
  return current || 'Not provided';
};

const EmergencyContactsSection: React.FC<SectionProps> = ({
  editMode,
  isMobile,
  handleEditToggle,
  handleSubmit,
  handleCancel,
  control,
  errors,
  getValues,
  carePlan
}) => {
  const theme = useTheme();
  const spacing = isMobile ? 1 : 2;

  const fields: { label: string; name: CarePlanFieldPath; icon: JSX.Element }[] = [
    {
      label: 'Husband Name',
      name: 'EmergencyContact.Husband.Name' as CarePlanFieldPath,
      icon: <FaUser />
    },
    {
      label: 'Husband Phone',
      name: 'EmergencyContact.Husband.Phone' as CarePlanFieldPath,
      icon: <FaPhone />
    },
    { label: 'Son Name', name: 'EmergencyContact.Son.Name' as CarePlanFieldPath, icon: <FaUser /> },
    {
      label: 'Son Phone',
      name: 'EmergencyContact.Son.Phone' as CarePlanFieldPath,
      icon: <FaPhone />
    },
    {
      label: 'If Not Home',
      name: 'ResponsePlanIfClientNotHome' as CarePlanFieldPath,
      icon: <FaStickyNote />
    }
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        mx: 'auto'
      }}
    >
      <SectionHeader
        icon={<FaExclamationTriangle size={isMobile ? 16 : 20} />}
        title="Emergency Contacts"
        color={theme.palette.error.dark}
        editMode={editMode}
        handleEditToggle={handleEditToggle}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        getValues={getValues}
      />

      <Box sx={{ p: isMobile ? 1 : 2 }}>
        {editMode ? (
          <Grid container spacing={spacing}>
            {fields.map((field) => (
              <Grid item xs={12} sm={6} key={field.name}>
                <CareTextField
                  label={field.label}
                  name={field.name}
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  icon={React.cloneElement(field.icon, { size: isMobile ? 14 : 16 })}
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={spacing}>
            {fields.map((field) => {
              const value = getNestedValue(carePlan, field.name);
              return (
                <Grid item xs={12} sm={6} key={field.name}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: isMobile ? 1 : 1.5,
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 1,
                      height: '100%',
                      minHeight: isMobile ? 56 : 64,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.error.light,
                        color: theme.palette.error.contrastText,
                        borderRadius: '50%',
                        width: isMobile ? 32 : 36,
                        height: isMobile ? 32 : 36,
                        flexShrink: 0
                      }}
                    >
                      {React.cloneElement(field.icon, {
                        size: isMobile ? 14 : 16
                      })}
                    </Box>
                    <Box sx={{ overflow: 'hidden', flex: 1 }}>
                      <Typography
                        variant={isMobile ? 'caption' : 'subtitle2'}
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.2
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        sx={{
                          fontWeight: 400,
                          color: theme.palette.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default EmergencyContactsSection;
